import type {
  PartnerCarrierRateSet,
  PartnerCarrierRateSetCreate,
  PartnerCarrierRateSetUpdate,
  PartnerSearchCarrierRateSetsParams,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { BulkLinkPayloads, LinkAssetPayload } from '../api-utils';
import { addToQueryListData, linkMultipleAssets, updateQueryListData } from '../api-utils';

type SingleAssetLinkType = '';

type ArrayAssetLinkType = 'tags' | 'carrierRates';

export const carrierRateSets = {
  search: {
    queryKey: 'partner-management-service/carrier-rate-sets',
    configurationType: 'cached' as const,
    fn: async (params: PartnerSearchCarrierRateSetsParams = {}) => {
      const { data } = await GatewayAxiosClient.get<PartnerCarrierRateSet[]>(
        '/partner-management-proxy/api/v1/partners/carrierRateSets',
        {
          params,
        },
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/carrier-rate-sets/id',
    configurationType: 'no-cache' as const,
    fn: async (carrierRateSetId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerCarrierRateSet>(
        `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}`,
      );

      return data;
    },
  },
  create: async (carrierRateSetCreate: PartnerCarrierRateSetCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerCarrierRateSet>(
      '/partner-management-proxy/api/v1/partners/carrierRateSets',
      carrierRateSetCreate,
    );

    return {
      data,
      refreshQueryKeys: [['partner-management-service/carrier-rate-sets']],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/carrier-rate-sets'],
          fn: (queryData: PartnerCarrierRateSet[]) => addToQueryListData(queryData, data),
        },
      ],
    };
  },
  update: async ({
    carrierRateSet,
    carrierRateSetId,
  }: {
    carrierRateSet: PartnerCarrierRateSetUpdate;
    carrierRateSetId: string;
  }) => {
    const { data } = await GatewayAxiosClient.put<PartnerCarrierRateSet>(
      `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}`,
      carrierRateSet,
    );

    return {
      data,
      refreshQueryKeys: [['partner-management-service/carrier-rate-sets']],
      updateQueryData: [
        { queryKey: ['partner-management-service/carrier-rate-sets/id', carrierRateSetId], data },
        {
          queryKey: ['partner-management-service/carrier-rate-sets'],
          fn: (queryData: PartnerCarrierRateSet[]) => updateQueryListData(queryData, data, 'carrierRateSetId'),
        },
      ],
    };
  },
  updateCurrency: async ({ carrierRateSetId, currencyCode }: { carrierRateSetId: string; currencyCode: string }) => {
    const { data } = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}/currency/${currencyCode}`,
    );

    return {
      data,
      // We don't know which carrier rates are affected by this, so we invalidate all carrier rates
      invalidateQueryKeys: [['partner-management-service/carrier-rates/summary']],
    };
  },
  link: async ({
    carrierRateSetId,
    payload,
  }: {
    carrierRateSetId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    const { data } = await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/carrierRateSets/` +
        `${carrierRateSetId}/${payload.assetType}/` +
        `${payload.assetId}`,
    });

    return {
      refreshQueryKeys: [['partner-management-service/carrier-rate-sets']],
      updateQueryData: [
        { queryKey: ['partner-management-service/carrier-rate-sets/id', carrierRateSetId], data },
        {
          queryKey: ['partner-management-service/carrier-rate-sets'],
          fn: (queryData: PartnerCarrierRateSet[]) => updateQueryListData(queryData, data, 'carrierRateSetId'),
        },
      ],
    };
  },
  bulkLink: async ({
    carrierRateSet,
    payloads,
  }: {
    carrierRateSet: PartnerCarrierRateSet;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerCarrierRateSet>(
      payloads,
      {
        carrierRates: carrierRateSet.carrierRateIds,
        tags: carrierRateSet.tagIds,
      },
      (payload) => carrierRateSets.link({ carrierRateSetId: carrierRateSet.carrierRateSetId!, payload }),
    );
  },
};
